<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import {
  ACCOUNT_EVENTS,
  CONVERSATION_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WhatsappForwardModal from 'dashboard/components-next/message/WhatsappForwardModal.vue';

export default {
  components: {
    AddCannedModal,
    MenuItem,
    ContextMenu,
    NextButton,
    WhatsappForwardModal,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close', 'replyTo'],
  setup() {
    const { getPlainText } = useMessageFormatter();

    return {
      getPlainText,
    };
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showDeleteModal: false,
      deleteScope: null,
      showEditModal: false,
      editContent: '',
      isSavingEdit: false,
      showReactionPicker: false,
      isSavingReaction: false,
      showForwardModal: false,
      reactionOptions: ['👍', '❤️', '😂', '😮', '😢', '🙏'],
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      getUISettings: 'getUISettings',
    }),
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    conversationId() {
      return this.message.conversation_id ?? this.message.conversationId;
    },
    messageId() {
      return this.message.id;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return useSnakeCase(
        this.message.content_attributes ?? this.message.contentAttributes
      );
    },
    ownReaction() {
      return (this.contentAttributes.whatsapp_reactions || []).find(
        reaction => reaction.from_me || reaction.actor_key === 'whatsapp_self'
      );
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      useTrack(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      this.showReactionPicker = false;
      this.$emit('close', e);
    },
    handleTranslate() {
      const { locale: accountLocale } = this.getAccount(this.currentAccountId);
      const agentLocale = this.getUISettings?.locale;
      const targetLanguage = agentLocale || accountLocale || 'en';
      this.$store.dispatch('translateMessage', {
        conversationId: this.conversationId,
        messageId: this.messageId,
        targetLanguage,
      });
      useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
      this.handleClose();
    },
    handleReplyTo() {
      this.$emit('replyTo', this.message);
      this.handleClose();
    },
    openDeleteModal(scope = null) {
      this.handleClose();
      this.deleteScope = scope;
      this.showDeleteModal = true;
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('deleteMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          deleteScope: this.deleteScope,
        });
        useAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
        this.handleClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deleteScope = null;
    },
    openEditModal() {
      this.handleClose();
      this.editContent = this.messageContent;
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editContent = '';
    },
    async confirmEdit() {
      if (!this.editContent.trim() || this.isSavingEdit) return;

      this.isSavingEdit = true;
      try {
        await this.$store.dispatch('editWhatsappMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          content: this.editContent.trim(),
        });
        useAlert(this.$t('CONVERSATION.CONTEXT_MENU.EDIT_SUCCESS'));
        this.closeEditModal();
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            this.$t('CONVERSATION.CONTEXT_MENU.EDIT_ERROR')
        );
      } finally {
        this.isSavingEdit = false;
      }
    },
    toggleReactionPicker() {
      this.showReactionPicker = !this.showReactionPicker;
    },
    async handleReaction(reaction) {
      if (this.isSavingReaction) return;

      this.isSavingReaction = true;
      const selectedReaction =
        this.ownReaction?.emoji === reaction ? '' : reaction;
      try {
        await this.$store.dispatch('reactWhatsappMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          reaction: selectedReaction,
        });
        useAlert(
          selectedReaction
            ? this.$t('CONVERSATION.CONTEXT_MENU.REACTION_SUCCESS')
            : this.$t('CONVERSATION.CONTEXT_MENU.REACTION_REMOVED')
        );
        this.handleClose();
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            this.$t('CONVERSATION.CONTEXT_MENU.REACTION_ERROR')
        );
      } finally {
        this.isSavingReaction = false;
      }
    },
    openForwardModal() {
      this.handleClose();
      this.showForwardModal = true;
    },
    closeForwardModal() {
      this.showForwardModal = false;
    },
  },
};
</script>

<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      v-model:show="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <AddCannedModal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Confirm Deletion -->
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      class="context-menu--delete-modal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeletion"
      :title="
        deleteScope === 'everyone'
          ? $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE_EVERYONE')
          : deleteScope === 'me'
            ? $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE_ME')
            : $t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')
      "
      :message="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')"
      :reject-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
    />
    <woot-modal
      v-if="showEditModal"
      v-model:show="showEditModal"
      :on-close="closeEditModal"
    >
      <div class="flex flex-col gap-4 p-6">
        <div>
          <h3 class="mb-1 text-base font-medium text-n-slate-12">
            {{ $t('CONVERSATION.CONTEXT_MENU.EDIT') }}
          </h3>
          <p class="m-0 text-sm text-n-slate-10">
            {{ $t('CONVERSATION.CONTEXT_MENU.EDIT_DESCRIPTION') }}
          </p>
        </div>
        <textarea
          v-model="editContent"
          class="min-h-28 w-full resize-y rounded-lg border border-n-strong bg-n-alpha-1 p-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          maxlength="4096"
        />
        <div class="flex justify-end gap-2">
          <NextButton
            slate
            faded
            :label="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
            @click="closeEditModal"
          />
          <NextButton
            :label="$t('CONVERSATION.CONTEXT_MENU.SAVE_EDIT')"
            :disabled="!editContent.trim()"
            :is-loading="isSavingEdit"
            @click="confirmEdit"
          />
        </div>
      </div>
    </woot-modal>
    <woot-modal
      v-if="showForwardModal"
      v-model:show="showForwardModal"
      :on-close="closeForwardModal"
    >
      <WhatsappForwardModal :message="message" @close="closeForwardModal" />
    </woot-modal>
    <NextButton
      v-if="!hideButton"
      ghost
      slate
      sm
      icon="i-lucide-ellipsis-vertical"
      class="invisible group-hover/context-menu:visible"
      @click="handleOpen"
    />
    <ContextMenu
      v-if="isOpen && !isCannedResponseModalOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
    >
      <div class="menu-container">
        <MenuItem
          v-if="enabledOptions['replyTo']"
          :option="{
            icon: 'arrow-reply',
            label: $t('CONVERSATION.CONTEXT_MENU.REPLY_TO'),
          }"
          variant="icon"
          @click.stop="handleReplyTo"
        />
        <MenuItem
          v-if="enabledOptions['reactWhatsapp']"
          :option="{
            icon: 'emoji',
            label: $t('CONVERSATION.CONTEXT_MENU.REACT'),
          }"
          variant="icon"
          @click.stop="toggleReactionPicker"
        />
        <div
          v-if="enabledOptions['reactWhatsapp'] && showReactionPicker"
          class="flex items-center gap-1 px-2 py-1"
        >
          <button
            v-for="reaction in reactionOptions"
            :key="reaction"
            type="button"
            class="flex size-8 items-center justify-center rounded-lg text-lg hover:bg-n-alpha-2 disabled:opacity-50"
            :class="{ 'bg-n-alpha-3': ownReaction?.emoji === reaction }"
            :disabled="isSavingReaction"
            @mousedown.prevent
            @click.stop="handleReaction(reaction)"
          >
            {{ reaction }}
          </button>
        </div>
        <MenuItem
          v-if="enabledOptions['editWhatsapp']"
          :option="{
            icon: 'edit',
            label: $t('CONVERSATION.CONTEXT_MENU.EDIT'),
          }"
          variant="icon"
          @click.stop="openEditModal"
        />
        <MenuItem
          v-if="enabledOptions['forwardWhatsapp']"
          :option="{
            icon: 'share',
            label: $t('CONVERSATION.CONTEXT_MENU.FORWARD'),
          }"
          variant="icon"
          @click.stop="openForwardModal"
        />
        <MenuItem
          v-if="enabledOptions['copy']"
          :option="{
            icon: 'clipboard',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY'),
          }"
          variant="icon"
          @click.stop="handleCopy"
        />
        <MenuItem
          v-if="enabledOptions['translate']"
          :option="{
            icon: 'translate',
            label: $t('CONVERSATION.CONTEXT_MENU.TRANSLATE'),
          }"
          variant="icon"
          @click.stop="handleTranslate"
        />
        <hr />
        <MenuItem
          v-if="enabledOptions['copyLink']"
          :option="{
            icon: 'link',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK'),
          }"
          variant="icon"
          @click.stop="copyLinkToMessage"
        />
        <MenuItem
          v-if="enabledOptions['cannedResponse']"
          :option="{
            icon: 'comment-add',
            label: $t('CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE'),
          }"
          variant="icon"
          @click.stop="showCannedResponseModal"
        />
        <hr
          v-if="
            enabledOptions['delete'] ||
            enabledOptions['deleteWhatsapp'] ||
            enabledOptions['deleteWhatsappForMe']
          "
        />
        <MenuItem
          v-if="enabledOptions['deleteWhatsappForMe']"
          :option="{
            icon: 'delete',
            label: $t('CONVERSATION.CONTEXT_MENU.DELETE_FOR_ME'),
          }"
          variant="icon"
          @click.stop="openDeleteModal('me')"
        />
        <MenuItem
          v-if="enabledOptions['deleteWhatsapp']"
          :option="{
            icon: 'delete',
            label: $t('CONVERSATION.CONTEXT_MENU.DELETE_FOR_EVERYONE'),
          }"
          variant="icon"
          @click.stop="openDeleteModal('everyone')"
        />
        <MenuItem
          v-if="enabledOptions['delete']"
          :option="{
            icon: 'delete',
            label: $t('CONVERSATION.CONTEXT_MENU.DELETE'),
          }"
          variant="icon"
          @click.stop="openDeleteModal"
        />
      </div>
    </ContextMenu>
  </div>
</template>

<style lang="scss" scoped>
.menu-container {
  @apply p-1 bg-n-background shadow-xl rounded-md;

  hr:first-child {
    @apply hidden;
  }

  hr {
    @apply m-1 border-b border-solid border-n-strong;
  }
}

.context-menu--delete-modal {
  :deep(.modal-container) {
    @apply max-w-[30rem];

    h2 {
      @apply font-medium text-base;
    }
  }
}
</style>
